import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');
const remiSync = createCloudRunAxios('remi-sync');

export const MANAGED_CRM_APPS = [
  'cinc', 'lofty', 'sierra-interactive', 'boldtrail',
] as const;

export const isManagedCrmApp = (app: string): boolean =>
  MANAGED_CRM_APPS.some((candidate) => candidate === app);

export type ProductionCrmConnectionStatus = {
  status: 'not_connected' | 'pending_setup' | 'connected' | 'sync_paused';
  app: string | null;
  sync_enabled: boolean;
  sync_direction: 'inbound' | 'bidirectional' | null;
  sync_eligible: boolean;
};

export type ProductionCrmMutation = {
  success: true;
  organization_id: string;
  app?: string;
  status: 'active' | 'disconnected';
  sync_enabled?: boolean;
};

export type CrmSyncDirection = 'inbound' | 'bidirectional';

/** Reconcile only the server-owned current binding after the atomic API transition. */
export const activateProductionCrmSync = async (
  orgId: string, app: string, direction: CrmSyncDirection
) => {
  if (!isManagedCrmApp(app)) throw new Error('Unsupported managed CRM app');
  const response = await remiSync.post<{status: 'ok'; message: string}>(
    '/webhook/crm/credential-connected',
    { organization_id: orgId, app, sync_direction: direction }
  );
  return response.data;
};

const path = (orgId: string) => `/api/v1/organizations/${encodeURIComponent(orgId)}/rollout`;

export const getProductionCrmConnectionStatus = async (orgId: string) => {
  const response = await remiApi.get<ProductionCrmConnectionStatus>(
    `${path(orgId)}/connection-status`
  );
  return response.data;
};

export const connectProductionCrm = async (
  orgId: string, credentialId: string, app: string
) => {
  if (!isManagedCrmApp(app)) throw new Error('Unsupported managed CRM app');
  if (!credentialId) throw new Error('CRM credential ID is invalid');
  const response = await remiApi.post<ProductionCrmMutation>(
    `${path(orgId)}/credential-connected`, { credential_id: credentialId, app }
  );
  return response.data;
};

export const disconnectProductionCrm = async (orgId: string, credentialId: string) => {
  const response = await remiApi.post<ProductionCrmMutation>(
    `${path(orgId)}/credential-deleted`, { credential_id: credentialId }
  );
  return response.data;
};

export const disconnectCurrentProductionCrm = async (orgId: string, app: string) => {
  if (!isManagedCrmApp(app)) throw new Error('Unsupported managed CRM app');
  const response = await remiApi.post<ProductionCrmMutation & { credential_id: string }>(
    `${path(orgId)}/disconnect-current`, { app }
  );
  return response.data;
};

export const promoteExistingProductionCrm = async (orgId: string) => {
  const response = await remiApi.post<ProductionCrmMutation>(
    `${path(orgId)}/promote-existing`, {}
  );
  return response.data;
};
